import React, { useState } from 'react'

import {
	MapPinIcon,
	MapIcon,
	ArrowTrendingUpIcon,
	SparklesIcon,
	CheckBadgeIcon,
} from '@heroicons/react/20/solid'

import ViewOnMap from './ViewOnMap'
import LoadPriceChart from './LoadPriceChart'

function LoadChart({
	load,
	isDarkMode,
	styles,
	isSimilarLoads,
	isMapModalOpen,
	closeMapModal,
	viewOnMapClick,
	isAdmin,
}) {
	const [CP, setCP] = useState(null)
	const [CPHistory, setCPHistory] = useState(null)
	const [data, setData] = useState(null)

	const handleCPLoad = (cpData) => {
		setCP(cpData)
	}

	const handleCPHistoryLoad = (cpHistoryData) => {
		setCPHistory(cpHistoryData)
	}

	const handleDataLoad = (dataInfo) => {
		setData(dataInfo)
	}

	const displayCP = CP || load?.CP

	const formatRelativeTime = timestamp => {
		const now = new Date()
		const date = new Date(timestamp)
		const diff = now.getTime() - date.getTime()
		const seconds = Math.floor(diff / 1000)
		const minutes = Math.floor(seconds / 60)
		const hours = Math.floor(minutes / 60)
		const days = Math.floor(hours / 24)

		if (minutes < 1) {
			return '1m ago'
		} else if (minutes < 60) {
			return `${minutes}m ago`
		} else if (hours < 24) {
			return `${hours}hr ago`
		} else {
			return `${days}d ago`
		}
	}

	return (
		<div>
			{' '}
			{/* Horizontal Divider */}
			<div className="col-span-12 h-px my-2"></div>
			<ViewOnMap
				stops={load?.loads}
				isModalOpen={isMapModalOpen}
				closeModal={closeMapModal}
				isDarkMode={isDarkMode}
			/>
			{/* Collapsible content goes here */}
			<div className="grid grid-cols-12 gap-2 md:gap-4">
				{/* Column 1: Load Stops - Hide if stops array exists */}
				{!load?.stops && (
					<div
						className={`col-span-12 md:col-span-3 ${
							isDarkMode ? 'loadcollapse-dark' : 'loadcollapse-light'
						} px-4 py-1 md:p-4 rounded`}
					>
						<div className="flex items-center gap-3 my-2 md:block md:gap-0 md:my-0">
							<div className="flex items-center md:mb-2">
								<MapPinIcon
									className="h-4 w-4 md:h-6 md:w-6 mr-1"
									aria-hidden="true"
								/>
								<h3 className="text-sm md:text-lg font-semibold">Stops</h3>{' '}
								{/* Add AI Bookable tag */}
								{load.isAIEnabled && (
									<div className="flex items-center ml-3">
										<SparklesIcon
											className="h-4 w-4 mr-1 text-purple-400"
											aria-hidden="true"
										/>
										<span className="text-sm font-semibold text-purple-400">
											AI Bookable
										</span>
									</div>
								)}
							</div>

							{!isSimilarLoads && (
								<button
									onClick={viewOnMapClick}
									className="flex items-center bg-cyan-500 text-white px-3 py-1 rounded md:mb-4"
								>
									<MapIcon
										className="h-4 w-4 md:h-6 md:w-6"
										aria-hidden="true"
									/>
									<span className="ml-2">View On Map</span>
								</button>
							)}
						</div>
						{load?.loads?.map((stop, index) => (
							<div className="flex items-center mb-2" key={index}>
								<span
									className={`${
										isDarkMode ? 'text-gray-200' : 'text-gray-700'
									} font-bold mr-2 text-xs md:text-sm`}
								>
									{index + 1}.
								</span>
								<p className="text-xs md:text-sm">
									{stop?.label && stop?.label} {stop?.city}, {stop?.state}
									<span
										className={`ml-2 ${
											isDarkMode ? 'text-blue-600' : 'text-blue-500'
										} text-bold`}
									>
										{stop?.isBobtail ? 'Bobtail' : ''}
									</span>
								</p>
							</div>
						))}
					</div>
				)}

				{/* Column 2: Broker Info */}
				<div
					className={`col-span-12 ${
						load?.stops ? 'md:col-span-4' : 'md:col-span-3'
					} ${
						isDarkMode ? 'loadcollapse-dark' : 'loadcollapse-light'
					} md:p-4 px-4 py-2 rounded`}
				>
					<div className="flex items-center gap-3 my-2 md:block md:gap-0 md:my-0">
						<div className="flex items-center md:mb-2">
							<CheckBadgeIcon
								className="h-4 w-4 md:h-6 md:w-6 mr-1"
								aria-hidden="true"
							/>
							<h3 className="text-sm md:text-lg font-semibold">Performance</h3>
						</div>

					</div>
					<p className="text-sm text-gray-400 mb-2">
						Accessible by performance scores
					</p>

					{/* Data Source Information - Admin Only */}
					{isAdmin && data && (
						<div className="mb-4 p-2 bg-gray-100 dark:bg-gray-700 rounded">
							<h4 className="text-xs font-semibold mb-1 text-gray-600 dark:text-gray-300">
								Data Source
							</h4>
							<div className="flex flex-wrap gap-2 text-xs">
								<span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded">
									{data.name}
								</span>
								{data.score && (
									<span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-0.5 rounded">
										Score: {data.score}
									</span>
								)}
								{data.extensionVersion && (
									<span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-0.5 rounded">
										{data.extensionVersion}
									</span>
								)}
								{data.deviceId && (
									<span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-0.5 rounded">
										{data.deviceId}
									</span>
								)}
								{data.time && (
									<span className="text-gray-500 dark:text-gray-400">
										{formatRelativeTime(data.time)}
									</span>
								)}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-1.5">
						{displayCP &&
							displayCP
								.sort((a, b) => {
									const order = {
										A_PLUS: 1,
										A: 2,
										B_PLUS: 3,
										B: 4,
										C_PLUS: 5,
										C: 6,
									}
									return order[a] - order[b]
								})
								.map((rating, index) => (
									<span
										key={index}
										className="px-2 py-0.5 rounded-full text-xs font-medium"
										style={{
											backgroundColor: rating.includes('A')
												? '#86efac'
												: rating.includes('B')
												? '#fde047'
												: '#fca5a5',
											color: '#1f2937',
										}}
									>
										{rating.replace('_PLUS', '+')}
									</span>
								))}
					</div>

					{isAdmin && CPHistory && CPHistory.length > 0 && (
						<div className="flex flex-col gap-2 mt-3">
							<span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
								History:
							</span>
							{CPHistory
								.filter(
									item =>
										item.performance !== 'NO_SCORE' &&
										!item.performance?.includes('D') &&
										item.performance !== 'F'
								)
								.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
								.map((item, index) => (
									<div key={index} className="flex items-center gap-2">
										<span
											className="px-2 py-0.5 rounded-full text-xs font-medium text-center"
											style={{
												backgroundColor: item.performance?.includes('A')
													? '#86efac'
													: item.performance?.includes('B')
													? '#fde047'
													: '#fca5a5',
												color: '#1f2937',
												minWidth: '32px',
											}}
										>
											{item.performance?.replace('_PLUS', '+')}
										</span>
										<span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
											{formatRelativeTime(item.timestamp)}
										</span>
									</div>
								))}
						</div>
					)}
				</div>

				{/* Column 3: Analytics */}
				<div
					className={`col-span-12 ${
						load?.stops ? 'md:col-span-8' : 'md:col-span-6'
					} ${
						isDarkMode ? 'loadcollapse-dark' : 'loadcollapse-light'
					} md:p-4 px-4 py-2 rounded`}
				>
					<div className="flex items-center gap-3 my-2 md:block md:gap-0 md:my-0">
						<div className="flex items-center md:mb-2">
							<ArrowTrendingUpIcon
								className="h-4 w-4 md:h-6 md:w-6 mr-1"
								aria-hidden="true"
							/>
							<h3 className="text-sm md:text-lg font-semibold">Analytics</h3>
						</div>

					</div>
					<LoadPriceChart isDarkMode={isDarkMode} load={load} onCPLoad={handleCPLoad} onCPHistoryLoad={handleCPHistoryLoad} onDataLoad={handleDataLoad} />
				</div>
			</div>
		</div>
	)
}

export default LoadChart
